import React, { useState, useRef, useEffect } from "react";
import * as S from "./MediaPlayer.styles";
import { ReactComponent as YoutubeIcon } from "@/assets/result/youtube_icon.svg?react";
import { ReactComponent as AudioFileIcon } from "@/assets/result/audio_icon.svg?react";
import { ReactComponent as VideoFileIcon } from "@/assets/result/video_icon.svg?react";
import { ReactComponent as CheckIcon } from "@/assets/result/check_icon.svg?react";
import SparkleIcon from "components/SpeakerEmotionBadge/SparkleIcon";
import { useMediaStore } from "store/mediaStore";

export interface MediaItem {
  id: string;
  title: string;
  src: string; // video / audio 실제 URL
  thumbnail?: string; // 리스트용 썸네일
  duration: string; // ex: "00:29:38"
  date: string; // ex: "2025.5.27"
  type: "youtube" | "audio" | "video";
  subtitleSrc?: string;
}

interface MediaPlayerProps {
  items: MediaItem[];
}

const MediaPlayer: React.FC<MediaPlayerProps> = ({ items }) => {
  const [current, setCurrent] = useState<MediaItem>(items[0]);
  const { setCurrentTime, seekTo, setSeekTo } = useMediaStore();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // When the items prop changes, reset the current item to the first one
    // in the new list. This prevents items from accumulating.
    if (items && items.length > 0) {
      setCurrent(items[0]);
    }
  }, [items]);

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    setCurrentTime(e.currentTarget.currentTime);
  };

  useEffect(() => {
    if (videoRef.current && seekTo !== null) {
      videoRef.current.currentTime = seekTo;
      setSeekTo(null); // Reset after seeking
    }
  }, [seekTo, setSeekTo]);

  return (
    <S.Container>
      {/* ▶ 현재 재생 중인 비디오 영역 */}
      <S.NowPlayingContainer>
        <S.VideoWrapper>
          <video
            ref={videoRef}
            key={current.id}
            src={current.src}
            poster={current.thumbnail}
            controls
            width="100%"
            onTimeUpdate={handleTimeUpdate}
          >
            {current.subtitleSrc && (
              <track
                kind="subtitles"
                src={current.subtitleSrc}
                label="Korean"
              />
            )}
          </video>
          <S.YoutubeBadge>
            <CheckIcon />
          </S.YoutubeBadge>
        </S.VideoWrapper>
        <S.NowInfo>
          <S.Title>{current.title}</S.Title>
          <S.Meta>
            <span>{current.duration}</span>
            <span>{current.date}</span>
          </S.Meta>
          {current.type === "youtube" && <YoutubeIcon />}
          {current.type === "audio" && <AudioFileIcon />}
          {current.type === "video" && <VideoFileIcon />}
        </S.NowInfo>
      </S.NowPlayingContainer>

      {/* ── 재생 대기 목록 ── */}
      {items
        .filter((item) => item.id !== current.id)
        .map((item) => (
          <S.Item
            key={item.id}
            $selected={false}
            onClick={() => setCurrent(item)}
          >
            <S.Thumb>
              {item.thumbnail ? (
                <img src={item.thumbnail} alt={item.title} />
              ) : (
                <SparkleIcon color="#ffffff" size={24} />
              )}
            </S.Thumb>
            <S.ItemInfo>
              <S.ItemTitle>{item.title}</S.ItemTitle>
              <S.Meta>
                <span>{item.duration}</span>
                <span>{item.date}</span>
              </S.Meta>
              {item.type === "audio" && <AudioFileIcon />}
              {item.type === "video" && <VideoFileIcon />}
              {item.type === "youtube" && <YoutubeIcon />}
            </S.ItemInfo>
          </S.Item>
        ))}
    </S.Container>
  );
};

export default MediaPlayer;
